import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Plus, Trash2 } from 'lucide-react';
import { formatPhoneNumber } from '../../pages/ClientIntake';

export default function MaritalStatusStep({ form, updateForm }) {
    const el = React.createElement;
    
    // Former spouse functions
    function addFormerSpouse() {
        const formerSpouses = form.former_spouses || [];
        updateForm({ 
            former_spouses: [...formerSpouses, { 
                id: String(Date.now()), 
                name: '', 
                date_of_birth: '',
                gender: '',
                disinherited: false 
            }] 
        });
    }
    
    function updateFormerSpouse(index, field, value) {
        const formerSpouses = [...(form.former_spouses || [])];
        formerSpouses[index] = { ...formerSpouses[index], [field]: value };
        updateForm({ former_spouses: formerSpouses });
    }
    
    function removeFormerSpouse(index) {
        updateForm({ former_spouses: (form.former_spouses || []).filter((_, i) => i !== index) });
    }
    
    const formerSpouses = form.former_spouses || [];
    
    return el('div', { className: 'space-y-6' },
        // Marital Status Selection
        el('div', { className: 'space-y-2' },
            el(Label, { className: 'text-base font-medium' }, 'Marital Status'),
            el('select', { 
                value: form.marital_status || 'single', 
                onChange: e => updateForm({ marital_status: e.target.value }), 
                className: 'w-full md:w-64 h-10 px-3 border border-stone-200 bg-white rounded-none', 
                'data-testid': 'marital-status-select' 
            },
                el('option', { value: 'single' }, 'Single'),
                el('option', { value: 'married' }, 'Married'),
                el('option', { value: 'divorced' }, 'Divorced'),
                el('option', { value: 'widowed' }, 'Widowed')
            )
        ),
        
        // Spouse Information (only if married)
        form.marital_status === 'married' && el('div', { className: 'p-4 border border-stone-200 space-y-4 mt-4' },
            el('h4', { className: 'font-serif text-lg' }, 'Spouse Information'),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Spouse Full Name'),
                    el(Input, { 
                        value: form.spouse?.name || '', 
                        onChange: e => updateForm({ spouse: { ...form.spouse, name: e.target.value } }), 
                        className: 'rounded-none', 
                        'data-testid': 'spouse-name-input' 
                    })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Spouse Pronouns'),
                    el('select', { 
                        value: form.spouse?.gender || '', 
                        onChange: e => updateForm({ spouse: { ...form.spouse, gender: e.target.value } }),
                        className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none',
                        'data-testid': 'spouse-gender-select'
                    },
                        el('option', { value: '' }, 'Select...'),
                        el('option', { value: 'male' }, 'He/Him'),
                        el('option', { value: 'female' }, 'She/Her'),
                        el('option', { value: 'neutral' }, 'They/Them')
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Email'),
                    el(Input, { 
                        type: 'email', 
                        value: form.spouse?.email || '', 
                        onChange: e => updateForm({ spouse: { ...form.spouse, email: e.target.value } }), 
                        className: 'rounded-none' 
                    })
                )
            ),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Spouse Date of Birth'),
                    el(Input, { 
                        type: 'date', 
                        value: form.spouse?.date_of_birth || '', 
                        onChange: e => updateForm({ spouse: { ...form.spouse, date_of_birth: e.target.value } }), 
                        className: 'rounded-none'
                    })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Spouse SSN (Last 4)'),
                    el(Input, { 
                        maxLength: 4,
                        value: form.spouse?.ssn_last_four || '', 
                        onChange: e => updateForm({ spouse: { ...form.spouse, ssn_last_four: e.target.value.replace(/\D/g, '') } }), 
                        className: 'rounded-none',
                        placeholder: 'XXXX'
                    })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Spouse Phone'),
                    el(Input, { 
                        type: 'tel',
                        value: form.spouse?.phone || '', 
                        onChange: e => updateForm({ spouse: { ...form.spouse, phone: formatPhoneNumber(e.target.value) } }), 
                        className: 'rounded-none',
                        placeholder: '(555) 555-5555'
                    })
                )
            )
        ),
        
        // Former Spouses Section (only if divorced)
        form.marital_status === 'divorced' && el('div', { className: 'p-4 border border-stone-200 space-y-4 mt-4', 'data-testid': 'former-spouses-section' },
            el('div', { className: 'flex items-center justify-between' },
                el('h4', { className: 'font-serif text-lg' }, 'Former Spouses'),
                el(Button, { 
                    type: 'button', 
                    variant: 'outline', 
                    size: 'sm', 
                    onClick: addFormerSpouse, 
                    className: 'rounded-none',
                    'data-testid': 'add-former-spouse-btn'
                },
                    el(Plus, { className: 'h-4 w-4 mr-2' }), 'Add Former Spouse'
                )
            ),
            el('p', { className: 'text-sm text-muted-foreground' }, 
                'Add former spouses here. They can be added as beneficiaries or disinherited in the Wills section.'
            ),
            formerSpouses.length === 0 
                ? el('p', { className: 'text-muted-foreground text-sm text-center py-4' }, 'No former spouses added')
                : formerSpouses.map((fs, i) => 
                    el('div', { 
                        key: fs.id, 
                        className: 'p-4 border space-y-4 ' + (fs.disinherited ? 'border-red-300 bg-red-50/30' : 'border-stone-200'),
                        'data-testid': `former-spouse-${i}`
                    },
                        el('div', { className: 'flex justify-between items-center' },
                            el('span', { className: 'text-sm font-medium' }, 
                                'Former Spouse ' + (i + 1) + (fs.disinherited ? ' (Disinherited)' : '')
                            ),
                            el(Button, { 
                                type: 'button', 
                                variant: 'ghost', 
                                size: 'icon', 
                                onClick: () => removeFormerSpouse(i), 
                                className: 'text-destructive h-8 w-8' 
                            },
                                el(Trash2, { className: 'h-4 w-4' })
                            )
                        ),
                        el('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                            el('div', { className: 'space-y-2' },
                                el(Label, null, 'Full Name'),
                                el(Input, { 
                                    value: fs.name || '', 
                                    onChange: e => updateFormerSpouse(i, 'name', e.target.value), 
                                    className: 'rounded-none',
                                    'data-testid': `former-spouse-name-${i}`
                                })
                            ),
                            el('div', { className: 'space-y-2' },
                                el(Label, null, 'Date of Birth'),
                                el(Input, { 
                                    type: 'date', 
                                    value: fs.date_of_birth || '', 
                                    onChange: e => updateFormerSpouse(i, 'date_of_birth', e.target.value), 
                                    className: 'rounded-none'
                                })
                            ),
                            el('div', { className: 'space-y-2' },
                                el(Label, null, 'Pronouns'),
                                el('select', { 
                                    value: fs.gender || '', 
                                    onChange: e => updateFormerSpouse(i, 'gender', e.target.value),
                                    className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none'
                                },
                                    el('option', { value: '' }, 'Select...'),
                                    el('option', { value: 'male' }, 'He/Him'),
                                    el('option', { value: 'female' }, 'She/Her'),
                                    el('option', { value: 'neutral' }, 'They/Them')
                                )
                            )
                        ),
                        el('div', { className: 'flex items-center space-x-2 pt-2' },
                            el(Checkbox, { 
                                id: 'disinherit-fs-' + i, 
                                checked: fs.disinherited || false, 
                                onCheckedChange: v => updateFormerSpouse(i, 'disinherited', v),
                                'data-testid': `disinherit-former-spouse-${i}`
                            }),
                            el(Label, { htmlFor: 'disinherit-fs-' + i, className: 'text-sm text-red-600 cursor-pointer' }, 'Disinherit this former spouse')
                        )
                    )
                )
        )
    );
}
